class DeliveryEstimation extends HTMLElement {
  constructor() {
    super();

    this.minDateElement = null;
    this.maxDateElement = null;
    this.countdownLineEl = null;
    this.staticLineEl = null;
    this.timerEl = null;
    this.prefixOutEl = null;
    this.suffixOutEl = null;
    this.countdownTimerId = null;
    this.dispatchNoTimerHtmlTemplate = '';
    this.dispatchPrefixHtmlTemplate = '';
    this.dispatchSuffixHtmlTemplate = '';

    this.config = {
      locale: 'fr',
      minDays: 3,
      maxDays: 5,
      noSaturday: false,
      noSunday: false,
      dateFormat: 'in_letters',
      showWeekday: true,
      cutoffHour: null,
      dispatchCountdown: false,
      sameDayCutoffHour: 12,
      shipDays: [false, false, false, false, false, false, false],
    };
  }

  connectedCallback() {
    this.minDateElement = this.querySelector('[data-ref="min-date"]');
    this.maxDateElement = this.querySelector('[data-ref="max-date"]');
    this.countdownLineEl = this.querySelector('[data-ref="dispatch-countdown-line"]');
    this.staticLineEl = this.querySelector('[data-ref="dispatch-static-line"]');
    this.timerEl = this.querySelector('[data-ref="dispatch-timer"]');
    this.prefixOutEl = this.querySelector('[data-ref="dispatch-prefix-out"]');
    this.suffixOutEl = this.querySelector('[data-ref="dispatch-suffix-out"]');

    this.#loadConfiguration();
    this.#calculateAndDisplayDates();

    if (this.config.dispatchCountdown) {
      this.#loadDispatchCountdownHtmlTemplates();
      this.#initDispatchCountdown();
    }
  }

  disconnectedCallback() {
    if (this.countdownTimerId !== null) {
      clearInterval(this.countdownTimerId);
      this.countdownTimerId = null;
    }
  }

  #loadConfiguration() {
    this.config.locale = this.dataset.locale || 'fr';
    this.config.minDays = parseInt(this.dataset.minDays, 10) || 3;
    this.config.maxDays = parseInt(this.dataset.maxDays, 10) || 5;
    this.config.noSaturday = this.dataset.noSaturday === 'true';
    this.config.noSunday = this.dataset.noSunday === 'true';
    this.config.dateFormat = this.dataset.dateFormat || 'in_letters';
    this.config.showWeekday = this.dataset.showWeekday === 'true';

    const cutoffHour = this.dataset.cutoffHour;
    if (cutoffHour && cutoffHour !== '') {
      this.config.cutoffHour = parseInt(cutoffHour, 10);
    }

    this.config.dispatchCountdown = this.dataset.dispatchCountdown === 'true';
    this.config.sameDayCutoffHour = parseInt(this.dataset.sameDayCutoffHour, 10);
    if (Number.isNaN(this.config.sameDayCutoffHour)) {
      this.config.sameDayCutoffHour = 12;
    }

    for (let d = 0; d <= 6; d++) {
      const v = this.getAttribute(`data-ship-day-${d}`);
      this.config.shipDays[d] = v === 'true';
    }
  }

  #htmlFromTemplateOrFallback(tpl, fallbackHtml) {
    let html = tpl ? tpl.innerHTML.trim() : '';
    const textOnly = html.replace(/<[^>]*>/g, '').trim();
    if (/^t:[a-zA-Z0-9_.-]+$/.test(textOnly)) {
      html = '';
    }
    return html || fallbackHtml;
  }

  #loadDispatchCountdownHtmlTemplates() {
    const prefixTpl = this.querySelector('[data-ref="dispatch-prefix-html-source"]');
    const suffixTpl = this.querySelector('[data-ref="dispatch-suffix-html-source"]');
    const noTimerTpl = this.querySelector('[data-ref="dispatch-no-timer-html-source"]');

    this.dispatchPrefixHtmlTemplate = this.#htmlFromTemplateOrFallback(prefixTpl, 'Commandez dans');
    this.dispatchSuffixHtmlTemplate = this.#htmlFromTemplateOrFallback(
      suffixTpl,
      "pour une expédition aujourd'hui",
    );
    this.dispatchNoTimerHtmlTemplate = this.#htmlFromTemplateOrFallback(
      noTimerTpl,
      "Commandez aujourd'hui pour une expédition dès #date",
    );
  }

  #escapeHtml(plain) {
    if (plain == null || plain === '') return '';
    const div = document.createElement('div');
    div.textContent = plain;
    return div.innerHTML;
  }

  #replaceTokensInHtml(html, tokenToReplacement) {
    if (!html) return '';
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const keys = Object.keys(tokenToReplacement).sort((a, b) => b.length - a.length);

    const walk = (parent) => {
      for (const node of [...parent.childNodes]) {
        if (node.nodeType === Node.TEXT_NODE) {
          let value = node.nodeValue;
          for (const key of keys) {
            const replacement = tokenToReplacement[key];
            if (!key || replacement === undefined) continue;
            if (value.includes(key)) {
              value = value.split(key).join(replacement);
            }
          }
          node.nodeValue = value;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          walk(node);
        }
      }
    };

    walk(tpl.content);
    return tpl.innerHTML;
  }

  #showCountdownLine(show) {
    if (!this.countdownLineEl || !this.staticLineEl) return;

    if (show) {
      this.countdownLineEl.classList.remove('delivery-estimation__is-hidden');
      this.staticLineEl.classList.add('delivery-estimation__is-hidden');
      return;
    }

    this.countdownLineEl.classList.add('delivery-estimation__is-hidden');
    this.staticLineEl.classList.remove('delivery-estimation__is-hidden');
  }

  #initDispatchCountdown() {
    const isPreview = this.hasAttribute('data-visual-preview-mode');

    if (isPreview) {
      const todayAnchor = new Date();
      todayAnchor.setHours(0, 0, 0, 0);
      if (this.prefixOutEl) {
        this.prefixOutEl.innerHTML = this.#applyDatePlaceholderToHtml(this.dispatchPrefixHtmlTemplate, todayAnchor);
      }
      if (this.suffixOutEl) {
        this.suffixOutEl.innerHTML = this.#applyDatePlaceholderToHtml(this.dispatchSuffixHtmlTemplate, todayAnchor);
      }
      this.#showCountdownLine(true);
      if (this.timerEl) {
        this.timerEl.textContent = '2h 15m 00s';
      }
      return;
    }

    this.#tickDispatchCountdown();
    this.countdownTimerId = window.setInterval(() => this.#tickDispatchCountdown(), 1000);
  }

  #tickDispatchCountdown() {
    if (!this.countdownLineEl || !this.staticLineEl || !this.timerEl) return;

    const now = new Date();
    const state = this.#getDispatchUiState(now);

    if (state.type === 'countdown') {
      const ms = state.deadline.getTime() - now.getTime();
      if (ms <= 0) {
        this.#tickDispatchCountdown();
        return;
      }
      const todayAnchor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (this.prefixOutEl) {
        this.prefixOutEl.innerHTML = this.#applyDatePlaceholderToHtml(this.dispatchPrefixHtmlTemplate, todayAnchor);
      }
      if (this.suffixOutEl) {
        this.suffixOutEl.innerHTML = this.#applyDatePlaceholderToHtml(this.dispatchSuffixHtmlTemplate, todayAnchor);
      }
      this.#showCountdownLine(true);
      this.timerEl.textContent = this.#formatDuration(ms);
      return;
    }

    this.#showCountdownLine(false);
    const nextShipDate = this.#findNextShipDateAfterToday(now);
    this.staticLineEl.innerHTML = this.#buildNoTimerHtml(nextShipDate);
  }

  #findNextShipDateAfterToday(now) {
    const candidate = new Date(now);
    candidate.setDate(candidate.getDate() + 1);
    candidate.setHours(0, 0, 0, 0);

    for (let i = 0; i < 370; i++) {
      if (this.config.shipDays[candidate.getDay()] === true) {
        return new Date(candidate);
      }
      candidate.setDate(candidate.getDate() + 1);
    }

    return null;
  }

  #buildNoTimerHtml(nextShipDate) {
    const fullDate = this.#escapeHtml(nextShipDate ? this.#formatDateSafe(nextShipDate, { forceWeekday: true }) : '');
    const templateHtml = this.dispatchNoTimerHtmlTemplate || '';

    return this.#replaceTokensInHtml(templateHtml, { '#date': fullDate });
  }

  #applyDatePlaceholderToHtml(html, date) {
    if (!html) return '';
    const formatted = date ? this.#formatDateSafe(date, { forceWeekday: true }) : '';
    const escaped = this.#escapeHtml(formatted);
    return this.#replaceTokensInHtml(html, { '#date': escaped });
  }

  /**
   * @returns {{ type: 'countdown', deadline: Date } | { type: 'static' }}
   */
  #getDispatchUiState(now) {
    const day = now.getDay();
    const shipsToday = this.config.shipDays[day] === true;
    const deadline = this.#cutoffDateForLocalDay(now, this.config.sameDayCutoffHour);

    if (shipsToday && now.getTime() < deadline.getTime()) {
      return { type: 'countdown', deadline };
    }

    return { type: 'static' };
  }

  #cutoffDateForLocalDay(referenceDate, hour) {
    const d = new Date(referenceDate);
    d.setHours(hour, 0, 0, 0);
    return d;
  }

  #formatDuration(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    if (h > 0) {
      return `${h}h ${pad(m)}m ${pad(s)}s`;
    }
    if (m > 0) {
      return `${m}m ${pad(s)}s`;
    }
    return `${s}s`;
  }

  #calculateAndDisplayDates() {
    const minDate = this.#calculateDeliveryDate(this.config.minDays);
    const maxDate = this.#calculateDeliveryDate(this.config.maxDays);

    if (this.minDateElement) {
      this.minDateElement.textContent = this.#formatDateSafe(minDate);
    }

    if (this.maxDateElement) {
      this.maxDateElement.textContent = this.#formatDateSafe(maxDate);
    }
  }

  #calculateDeliveryDate(daysToAdd) {
    const now = new Date();
    let startDate = new Date();

    if (this.config.cutoffHour !== null) {
      const currentHour = now.getHours();
      if (currentHour >= this.config.cutoffHour) {
        startDate.setDate(startDate.getDate() + 1);
      }
    }

    let businessDaysAdded = 0;
    const currentDate = new Date(startDate);

    while (businessDaysAdded < daysToAdd) {
      currentDate.setDate(currentDate.getDate() + 1);

      const dayOfWeek = currentDate.getDay();
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;

      const isBusinessDay = !((isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday));

      if (isBusinessDay) {
        businessDaysAdded++;
      }
    }

    while (this.#isNonDeliveryDay(currentDate)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return currentDate;
  }

  #isNonDeliveryDay(date) {
    const dayOfWeek = date.getDay();
    const isSaturday = dayOfWeek === 6;
    const isSunday = dayOfWeek === 0;

    return (isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday);
  }

  #formatDateSafe(date, { forceWeekday = false } = {}) {
    if (!date || Number.isNaN(date.getTime())) return '';

    const options = {};

    if (this.config.dateFormat === 'numbers') {
      options.day = 'numeric';
      options.month = 'numeric';
    } else {
      options.day = 'numeric';
      options.month = 'long';
    }

    if (forceWeekday || this.config.showWeekday) {
      options.weekday = 'long';
    }

    const locale = String(this.config.locale || 'fr').replace(/_/g, '-');

    try {
      const formatter = new Intl.DateTimeFormat(locale, options);
      let formattedDate = formatter.format(date);
      formattedDate = this.#capitalizeFirstLetter(formattedDate);
      return formattedDate;
    } catch {
      try {
        const formatter = new Intl.DateTimeFormat('fr-FR', options);
        let formattedDate = formatter.format(date);
        formattedDate = this.#capitalizeFirstLetter(formattedDate);
        return formattedDate;
      } catch {
        return '';
      }
    }
  }

  #capitalizeFirstLetter(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

if (!customElements.get('delivery-estimation')) {
  customElements.define('delivery-estimation', DeliveryEstimation);
}
